import { createSelector } from 'reselect';
import {
  filter,
  find,
  join,
  has,
  hasPath,
  length,
  map,
  path,
  pathEq,
  pathOr,
  pipe,
  prop,
  propEq,
  propOr,
  tail,
  test,
  both,
  applySpec,
  values,
} from 'ramda';

import i18n from 'services/i18n';
import { formatDate } from 'utilities/formatters';
import { NA } from 'constants/common';
import { getPatientDeviceType } from '../patientsData';

const localState = prop('questionnaireResponses');

export const getQuestionnaireResponseById = id =>
  createSelector(
    localState,
    prop(id),
  );

export const getQuestionnaireResponseLoadingStatus = id =>
  createSelector(
    getQuestionnaireResponseById(id),
    propOr(true, 'loading'),
  );

export const getQuestionnaireResponseData = id =>
  createSelector(
    getQuestionnaireResponseById(id),
    prop('data'),
  );

export const getQuestionnaireResponseShouldShowLoading = id =>
  createSelector(
    [getQuestionnaireResponseLoadingStatus(id), getQuestionnaireResponseData(id)],
    (loading, data) => loading && !data,
  );

const getQuestionnaireResponseItemById = (id, linkId) =>
  createSelector(
    getQuestionnaireResponseData(id),
    pipe(
      prop('item'),
      find(propEq('linkId', linkId)),
    ),
  );

export const getPrescribedMedicationField = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'medications'),
    pipe(
      prop('item'),
      pathOr('', ['0', 'item', '0', 'answer', '0', 'valueCoding', 'display']),
    ),
  );

export const getMedicationsItem = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'medications'),
    pipe(
      prop('item'),
      filter(
        ({ item }) =>
          hasPath(['0', 'answer'])(item) ||
          hasPath(['1', 'answer'])(item) ||
          hasPath(['3', 'answer'])(item),
      ),
    ),
  );

export const getFormattedMedications = id =>
  createSelector(
    getMedicationsItem(id),
    map(({ item }) =>
      applySpec({
        name: pathOr(NA, ['0', 'answer', '0', 'valueString']),
        dose: pathOr(NA, ['1', 'answer', '0', 'valueString']),
        unit: pathOr('', ['2', 'answer', '0', 'valueCoding', 'display']),
        freq: pathOr(NA, ['3', 'answer', '0', 'valueCoding', 'display']),
      })(item),
    ),
  );

export const getMedicalHistoryMainItems = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'medical_hist'),
    pipe(
      prop('item'),
      // filter(it => it.linkId !== 'medical_hist_oth'),
    ),
  );

export const getMedicalHistoryAdditionalItems = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'medical_hist'),
    pipe(
      propOr([], 'item'),
      filter(it => it.linkId === 'medical_hist_oth' && it.answer),
      map(val => ({
        linkId: 'medical_hist_oth',
        text: pathOr('', ['0', 'valueString'], val.answer),
        answer: [
          {
            valueBoolean: true,
          },
        ],
      })),
    ),
  );

export const getMedicalHistoryItem = id =>
  createSelector(
    getMedicalHistoryMainItems(id),
    // getMedicalHistoryAdditionalItems(id),
    // (mainValues, additionalValues) => [...mainValues, ...additionalValues],
    mainvalues => [...mainvalues],
  );
export const getIcdIndProCodeChoice = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'icd_ind_pro1'),
    pathOr('', ['answer', '0', 'valueCoding', 'display']),
  );

export const getIcdIndProCodeOther = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'icd_ind_pro1_othsp'),
    pathOr('', ['answer', '0', 'valueString']),
  );

export const getIcdInd = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'icd_ind'),
    pathOr('N/A', ['answer', '0', 'valueCoding', 'display']),
  );

export const getIcdIndDisplay = id =>
  createSelector(
    getIcdIndProCodeChoice(id),
    getIcdIndProCodeOther(id),
    getIcdInd(id),
    (choiceVal, otherVal, icdInd) =>
      `${icdInd} ${choiceVal || otherVal ? `(${otherVal || choiceVal})` : ''}`,
  );

export const getIcdIndCode = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'icd_ind'),
    pathOr('N/A', ['answer', '0', 'valueCoding', 'code']),
  );

export const getIcdIndProCode2Choice = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'icd_ind_pro2'),
    pathOr('', ['answer', '0', 'valueCoding', 'display']),
  );

export const getIcdIndProCode2Other = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'icd_ind_pro2_othsp'),
    pathOr('', ['answer', '0', 'valueString']),
  );

export const getIcdIndSec = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'icd_ind_sec'),
    pathOr('N/A', ['answer', '0', 'valueCoding', 'display']),
  );
export const getIcdIndSecDisplay = id =>
  createSelector(
    getIcdIndProCode2Choice(id),
    getIcdIndProCode2Other(id),
    getIcdIndSec(id),
    (choiceVal, otherVal, icdInd) =>
      `${icdInd} ${choiceVal || otherVal ? `(${otherVal || choiceVal})` : ''}`,
  );

export const getPacemakerIndication = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'pacemaker_ind'),
    pathOr('--', ['answer', '0', 'valueCoding', 'display']),
  );

export const getPacemakerIndicationOther = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'othpaceind_spec'),
    pathOr('--', ['answer', '0', 'valueCoding', 'display']),
  );

export const getPacemakerIndicationDisplay = id =>
  createSelector(
    getPacemakerIndication(id),
    getPacemakerIndicationOther(id),
    (paceInd, otherVal) => `${paceInd} ; ${otherVal}`,
  );

export const getIndicationForImplant = id =>
  createSelector(
    getPatientDeviceType(id),
    getPacemakerIndicationDisplay(id),
    getIcdIndDisplay(id),
    (deviceType, pacemakerDisplay, icdDisplay) =>
      deviceType === 'Pacemaker' ? pacemakerDisplay : icdDisplay,
  );

export const getNyhaClassDisplay = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'nyha'),
    pathOr('N/A', ['answer', '0', 'valueCoding', 'display']),
  );

export const getCardiacRisksActive = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'cardiac_risk'),
    pipe(
      prop('item'),
      filter(pathEq(['answer', '0', 'valueBoolean'], true)),
    ),
  );

export const getClinicPhone = id =>
  createSelector(
    getQuestionnaireResponseData(id),
    pipe(
      propOr([], 'item'),
      find(propEq('linkId', 'device_phone_urgent')),
      pathOr('', ['answer', '0', 'valueString']),
    ),
  );

export const getClinicName = id =>
  createSelector(
    getQuestionnaireResponseData(id),
    pipe(
      propOr([], 'item'),
      find(propEq('linkId', 'device_remotetransclin')),
      pathOr('', ['answer', '0', 'valueString']),
    ),
  );

export const getEfValue = id =>
  createSelector(
    getQuestionnaireResponseData(id),
    pipe(
      prop('item'),
      find(propEq('linkId', 'eject_fraction')),
      path(['answer', '0', 'valueString']),
    ),
  );

export const getEfDate = id =>
  createSelector(
    getQuestionnaireResponseData(id),
    pipe(
      prop('item'),
      find(propEq('linkId', 'eject_fraction_do')),
      path(['answer', '0', 'valueDate']),
      formatDate(i18n.t('dateTimeFormat.slashesDate')),
    ),
  );

export const getFrailityScore = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'frailty_score'),
    pipe(
      pathOr('N/A', ['answer', '0', 'valueCoding', 'display']),
      value => [
        {
          title: i18n.t('medicalInformation.frailtyScore'),
          value,
        },
      ],
    ),
  );

export const getHospitalizations = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'hospitalizations'),
    pipe(
      propOr([], 'item'),
      map(item => ({
        title: pipe(
          pathOr([], ['item']),
          find(prop(['answer'])),
          pathOr([], ['answer']),
          find(prop('valueDate')),
          values(),
        )(item),
        value: pipe(
          propOr([], 'item'),
          tail,
          filter(has('answer')),
          map(path(['answer', '0', 'valueCoding', 'display'])),
          join(', '),
          // value => (length(value) ? value : NA),
        )(item),
      })),
      filter(({ title, value }) => title && value),
    ),
  );

export const getQuestionnaireResponseBaselineBloodwork = (id, notTableView = false) =>
  createSelector(
    getQuestionnaireResponseData(id),
    pipe(
      propOr([], 'item'),
      items => {
        const baseCrt = pipe(
          find(propEq('linkId', 'base_crt')),
          pathOr(NA, ['answer', '0', 'valueString']),
        )(items);
        const baseCrtDo = pipe(
          find(propEq('linkId', 'base_crt_do')),
          path(['answer', '0', 'valueDate']),
          date => (date ? formatDate(i18n.t('dateTimeFormat.slashesDate'))(date) : NA),
        )(items);
        const baseHgb = pipe(
          find(propEq('linkId', 'base_hgb')),
          pathOr(NA, ['answer', '0', 'valueString']),
        )(items);
        const baseHgbDo = pipe(
          find(propEq('linkId', 'base_hgb_do')),
          path(['answer', '0', 'valueDate']),
          date => (date ? formatDate(i18n.t('dateTimeFormat.slashesDate'))(date) : NA),
        )(items);
        const baseSodium = pipe(
          find(propEq('linkId', 'base_sodium')),
          pathOr(NA, ['answer', '0', 'valueString']),
        )(items);
        const baseSodiumDo = pipe(
          find(propEq('linkId', 'base_sodium_do')),
          path(['answer', '0', 'valueDate']),
          date => (date ? formatDate(i18n.t('dateTimeFormat.slashesDate'))(date) : NA),
        )(items);
        const basePotassium = pipe(
          find(propEq('linkId', 'base_potassium')),
          pathOr(NA, ['answer', '0', 'valueString']),
        )(items);
        const basePotassiumDo = pipe(
          find(propEq('linkId', 'base_potassium_do')),
          path(['answer', '0', 'valueDate']),
          date => (date ? formatDate(i18n.t('dateTimeFormat.slashesDate'))(date) : NA),
        )(items);
        const eventCrt = pipe(
          find(propEq('linkId', 'event_crt')),
          pathOr(NA, ['answer', '0', 'valueString']),
        )(items);
        const eventCrtDo = pipe(
          find(propEq('linkId', 'event_crt_do')),
          path(['answer', '0', 'valueDate']),
          date => (date ? formatDate(i18n.t('dateTimeFormat.slashesDate'))(date) : NA),
        )(items);
        const eventHgb = pipe(
          find(propEq('linkId', 'event_hgb')),
          pathOr(NA, ['answer', '0', 'valueString']),
        )(items);
        const eventHgbDo = pipe(
          find(propEq('linkId', 'event_hgb_do')),
          path(['answer', '0', 'valueDate']),
          date => (date ? formatDate(i18n.t('dateTimeFormat.slashesDate'))(date) : NA),
        )(items);
        const eventSodium = pipe(
          find(propEq('linkId', 'event_sodium')),
          pathOr(NA, ['answer', '0', 'valueString']),
        )(items);
        const eventSodiumDo = pipe(
          find(propEq('linkId', 'event_sodium_do')),
          path(['answer', '0', 'valueDate']),
          date => (date ? formatDate(i18n.t('dateTimeFormat.slashesDate'))(date) : NA),
        )(items);
        const eventPotassium = pipe(
          find(propEq('linkId', 'event_potassium')),
          pathOr(NA, ['answer', '0', 'valueString']),
        )(items);
        const eventPotassiumDo = pipe(
          find(propEq('linkId', 'event_potassium_do')),
          path(['answer', '0', 'valueDate']),
          date => (date ? formatDate(i18n.t('dateTimeFormat.slashesDate'))(date) : NA),
        )(items);

        if (notTableView) {
          return [
            {
              title: i18n.t('medicalInformation.creatinine'),
              date: baseCrtDo,
              value: `${baseCrt} μmol/L`,
            },
            {
              title: i18n.t('medicalInformation.hemoglobin'),
              date: baseHgbDo,
              value: `${baseHgb} g/L`,
            },
            {
              title: i18n.t('medicalInformation.sodium'),
              date: baseSodiumDo,
              value: `${baseSodium} mmol/L`,
            },
            {
              title: i18n.t('medicalInformation.potassium'),
              date: basePotassiumDo,
              value: `${basePotassium} mmol/L`,
            },
            {
              title: i18n.t('medicalInformation.creatinine'),
              date: eventCrtDo,
              value: `${eventCrt} μmol/L`,
            },
            {
              title: i18n.t('medicalInformation.hemoglobin'),
              date: eventHgbDo,
              value: `${eventHgb} g/L`,
            },
            {
              title: i18n.t('medicalInformation.sodium'),
              date: eventSodiumDo,
              value: `${eventSodium} mmol/L`,
            },
            {
              title: i18n.t('medicalInformation.potassium'),
              date: eventPotassiumDo,
              value: `${eventPotassium} mmol/L`,
            },
          ];
        }

        return [
          {
            title: i18n.t('medicalInformation.creatinine'),
            value: `${baseCrtDo}, ${baseCrt} μmol/L`,
          },
          {
            title: i18n.t('medicalInformation.hemoglobin'),
            value: `${baseHgbDo}, ${baseHgb} g/L`,
          },
          {
            title: i18n.t('medicalInformation.sodium'),
            value: `${baseSodiumDo}, ${baseSodium} mmol/L`,
          },
          {
            title: i18n.t('medicalInformation.potassium'),
            value: `${basePotassiumDo}, ${basePotassium} mmol/L`,
          },
          {
            title: i18n.t('medicalInformation.creatinine'),
            value: `${eventCrtDo}, ${eventCrt} μmol/L`,
          },
          {
            title: i18n.t('medicalInformation.hemoglobin'),
            value: `${eventHgbDo}, ${eventHgb} g/L`,
          },
          {
            title: i18n.t('medicalInformation.sodium'),
            value: `${eventSodiumDo}, ${eventSodium} mmol/L`,
          },
          {
            title: i18n.t('medicalInformation.potassium'),
            value: `${eventPotassiumDo}, ${eventPotassium} mmol/L`,
          },
        ];
      },
    ),
  );

export const getEfMethod = id =>
  createSelector(
    getQuestionnaireResponseData(id),
    pipe(
      prop('item'),
      find(propEq('linkId', 'eject_fraction_method')),
      path(['answer', '0', 'valueCoding', 'display']),
    ),
  );

export const getArtialArrhythmia = id =>
  createSelector(
    getQuestionnaireResponseData(id),
    pipe(
      prop('item'),
      find(propEq('linkId', 'b_atrarr')),
      path(['answer', '0', 'valueCoding', 'code']),
    ),
  );

export const getArtialArrhythmiaType = id =>
  createSelector(
    getQuestionnaireResponseData(id),
    pipe(
      prop('item'),
      find(propEq('linkId', 'b_atrarr_type')),
      path(['answer', '0', 'valueCoding', 'display']),
    ),
  );

export const getPacemakerDependent = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_ppm_depend'),
    path(['answer', '0', 'valueCoding', 'display']),
  );

export const getPriorVentricularArrhythmia = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_venarr'),
    path(['answer', '0', 'valueCoding', 'code']),
  );

export const getPriorVentricularArrhythmiaRecent = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_dev_treat'),
    pathOr(NA, ['answer', '0', 'valueCoding', 'display']),
  );

export const getPriorVentricularArrhythmiaRecentDate = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_dev_treat_do'),
    pipe(
      path(['answer', '0', 'valueDate']),
      formatDate(i18n.t('dateTimeFormat.slashesDate')),
    ),
  );

export const getIsPriorCurrentTreatment = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_va_treat'),
    pathOr('', ['answer', '0', 'valueCoding', 'display']),
  );

export const getIsAntiarrhythmicMedication = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_va_treat_aad'),
    pathOr('', ['answer', '0', 'valueCoding', 'display']),
  );

export const getAntiarrhythmicMedication = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_va_treat_aad_name'),
    pathOr('', ['answer', '0', 'valueCoding', 'display']),
  );

export const getIsCatheter = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_va_treat_cath'),
    pathOr('', ['answer', '0', 'valueCoding', 'display']),
  );

export const getIsAntiarrhythmicMedsDiscontinued = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_va_treat_aaddis'),
    pathOr('', ['answer', '0', 'valueCoding', 'display']),
  );

export const getTreatmentType = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_dev_treat_type'),
    pipe(
      prop('item'),
      filter(pathEq(['answer', '0', 'valueBoolean'], true)),
      map(path(['text'])),
      join(', '),
    ),
  );

export const getPriorVentricularArrhythmiaTreatment = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_venarr_treat_dev'),
    pipe(
      propOr([], 'item'),
      filter(pathEq(['answer', '0', 'valueBoolean'], true)),
    ),
  );

export const getPriorVentricularArrhythmiaTreatmentOther = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_venarr_treat_oth'),
    pipe(
      prop('item'),
      filter(pathEq(['answer', '0', 'valueBoolean'], true)),
    ),
  );

export const getPriorVentricularArrhythmiaTreatmentOtherAdditional = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'b_venarr_treat_oth'),
    pipe(
      prop('item'),
      filter(
        both(propEq('linkId', 'b_venarr_treat_oth_sp'), hasPath(['answer', '0', 'valueString'])),
      ),
    ),
  );

export const getPriorVentricularArrhythmiaTreatmentAll = id =>
  createSelector(
    getPriorVentricularArrhythmiaTreatment(id),
    getPriorVentricularArrhythmiaTreatmentOther(id),
    getPriorVentricularArrhythmiaTreatmentOtherAdditional(id),
    (treat, treatOth, treatOthAdd) => [...treat, ...treatOth, ...treatOthAdd],
  );

export const getPatientSex = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'sex'),
    path(['answer', '0', 'valueCoding', 'display']),
  );

export const getPatientHeartDiseaseString = id =>
  createSelector(
    getMedicalHistoryItem(id),
    pipe(
      filter(pathEq(['answer', '0', 'valueBoolean'], true)),
      map(prop('text')),
      join(', '),
    ),
  );

export const getQuestionnaireResponseItem = id =>
  createSelector(
    getQuestionnaireResponseById(id),
    pathOr([], ['data', 'item']),
  );

export const getPatientQuestionnaireResponseDeviceImplant = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      find(propEq('linkId', 'deviceimplant')),
      pathOr(NA, ['answer', '0', 'valueCoding', 'display']),
    ),
  );

export const getPatientQuestionnaireResponseDeviceImplantDate = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      find(propEq('linkId', 'device_do_implant')),
      path(['answer', '0', 'valueDate']),
      valueDate => (valueDate ? formatDate(i18n.t('dateTimeFormat.dateOfImplant'))(valueDate) : NA),
    ),
  );

export const getPatientQuestionnaireResponseDeviceClinicSpecial = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      find(propEq('linkId', 'device_follow')),
      propOr([], 'item'),
      find(propEq('linkId', 'device_clinic_special')),
      pathOr(NA, ['answer', '0', 'valueString']),
    ),
  );

export const getPatientQuestionnaireResponseSatelliteDeviceClinic = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      find(propEq('linkId', 'device_follow')),
      propOr([], 'item'),
      find(propEq('linkId', 'device_satname')),
      pathOr(NA, ['answer', '0', 'valueString']),
    ),
  );

export const getPatientQuestionnaireResponseLocalDeviceClinicPhone = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      find(propEq('linkId', 'device_phone_urgent')),
      pathOr(NA, ['answer', '0', 'valueString']),
    ),
  );

export const getPatientQuestionnaireResponseDeviceName = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      find(propEq('linkId', 'model_name')),
      pathOr(NA, ['answer', '0', 'valueString']),
    ),
  );

export const getPatientQuestionnaireResponseSerialNumber = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      find(propEq('linkId', 'model_serialnum')),
      pathOr(NA, ['answer', '0', 'valueString']),
    ),
  );

export const getPatientQuestionnaireResponseDeviceVendor = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      find(propEq('linkId', 'manufacturer')),
      pathOr(NA, ['answer', '0', 'valueCoding', 'display']),
    ),
  );

export const getPatientQuestionnaireResponseActiveImplantsCount = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      filter(({ linkId, answer }) => test(/lead_active_\d+$/, linkId) && answer),
      length,
    ),
  );

export const getPatientQuestionnaireResponseInactiveImplantsCount = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      filter(({ linkId, answer }) => test(/lead_inactive_\d+$/, linkId) && answer),
      length,
    ),
  );

export const getPatientQuestionnaireResponseHomeMonitor = id =>
  createSelector(
    getQuestionnaireResponseItem(id),
    pipe(
      find(propEq('linkId', 'home_monitor')),
      pathOr(NA, ['answer', '0', 'valueCoding', 'display']),
    ),
  );

// checks if patient have completed their 18 month visit
export const isVisitComplete = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'f_visit_complete'),
    pathOr('No', ['answer', '0', 'valueCoding', 'display']),
  );

export const isAdvisory = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'f_dev_advise'),
    pathOr('No', ['answer', '0', 'valueCoding', 'display']),
  );

export const getDeviceAdvisoryType = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'f_dev_advise_sp'),
    pathOr('N/A', ['answer', '0', 'valueCoding', 'display']),
  );

export const getDeviceAdvisoryLeadType = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'f_dev_advise_lead'),
    pathOr('N/A', ['answer', '0', 'valueCoding', 'display']),
  );

export const getDeviceAdvisoryModel = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'f_dev_advise_model'),
    pathOr('N/A', ['answer', '0', 'valueString']),
  );

export const getDeviceAdvisorySerialNumber = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'f_dev_advise_serialnum'),
    pathOr('N/A', ['answer', '0', 'valueString']),
  );

export const getAlertDevice1Model = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'alert_device1_model'),
    pathOr('N/A', ['answer', '0', 'valueString']),
  );

export const getAlertDevice2Model = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'alert_device2_model'),
    pathOr('N/A', ['answer', '0', 'valueString']),
  );

export const getAlertDevice1 = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'alert_device1'),
    pathOr('N/A', ['answer', '0', 'valueCoding', 'display']),
  );

export const getAlertDevice2 = id =>
  createSelector(
    getQuestionnaireResponseItemById(id, 'alert_device2'),
    pathOr('N/A', ['answer', '0', 'valueCoding', 'display']),
  );
